import { KibContainer } from '@chewy/kib-layout-react';
import {
  AnalyticsAction,
  AnalyticsObjectType,
  AnalyticsParentPage,
  AnalyticsPlacementType,
  AnalyticsSubPage,
} from '@chewy/loyalty-common/constants';
import { useIntersectionObserver } from '@chewy/loyalty-common/hooks';
import { ThemedAsset } from '@chewy/loyalty-common/ThemedAsset';
import { buildLoyaltyAnalyticsEvent } from '@chewy/loyalty-common/utils';
import { WaveDivider } from '@chewy/loyalty-common/WaveDivider';
import clsx from 'clsx';
import { useRef } from 'react';
import { Trans } from 'react-i18next';

import { LandingPagePlacementDetail } from '@/analytics/landing-page';
import {
  buildMembershipDashboardAnalyticsEvent,
  MembershipDashboardPlacementDetail,
} from '@/analytics/membership-dashboard';
import { LoyaltyAsset, LoyaltyAssetMap } from '@/assets';
import { CmsContent } from '@/components/common/CmsContent/CmsContent';
import { ChewyPlusTerms } from '@/components/membership-dashboard/ChewyPlusTerms/ChewyPlusTerms';
import { useAnalyticsContext } from '@/providers/AnalyticsProvider';
import {
  MEMBERSHIP_DASHBOARD_SELECTION_KEY,
  SIGN_UP_V2_SELECTION_KEY,
} from '@/services/content-a/fetch-general-page';

import styles from './FaqSection.module.scss';

const FAQ_CONFIG = {
  dashboard: {
    cmsSelectionKey: MEMBERSHIP_DASHBOARD_SELECTION_KEY,
    cmsZones: [1],
    headingI18nKey: 'loyalty.membership.dashboard.v4.faq.heading',
    testIdPrefix: 'membership-dashboard-v4',
  },
  signup: {
    cmsSelectionKey: SIGN_UP_V2_SELECTION_KEY,
    cmsZones: [7],
    headingI18nKey: 'loyalty.sign-up.v4.faq.heading',
    testIdPrefix: 'sign-up-page-v4',
  },
} as const;

interface FaqSectionProps {
  isNma?: boolean;
  showWebExperience?: boolean;
  variant: keyof typeof FAQ_CONFIG;
}

export const FaqSection = ({
  isNma = false,
  showWebExperience = true,
  variant,
}: FaqSectionProps) => {
  const { trackSegmentEvent } = useAnalyticsContext();
  const viewed = useRef(false);

  const handleSectionVisibilityChange = (isVisible: boolean) => {
    if (!isVisible || viewed.current) return;
    viewed.current = true;
    if (variant === 'dashboard') {
      const event = buildMembershipDashboardAnalyticsEvent({
        action: AnalyticsAction.Impression,
        objectType: AnalyticsObjectType.Widget,
        placementDetail: MembershipDashboardPlacementDetail.Faq,
      });
      trackSegmentEvent(event.eventName, event.data);
      return;
    }

    const event = buildLoyaltyAnalyticsEvent({
      action: AnalyticsAction.Impression,
      objectType: AnalyticsObjectType.Widget,
      placementDetail: LandingPagePlacementDetail.Faq,
      subPage: AnalyticsSubPage.None,
      placementType: AnalyticsPlacementType.Landing,
      parentPage: AnalyticsParentPage.Landing,
    });
    trackSegmentEvent(event.eventName, {
      ...event.data,
      extraData: { experienceVersion: 'v4' },
    });
  };
  const { ref: impressionRef } = useIntersectionObserver({
    freezeOnceVisible: true,
    onChange: handleSectionVisibilityChange,
  });

  const { cmsSelectionKey, cmsZones, headingI18nKey, testIdPrefix } = FAQ_CONFIG[variant];

  const handleMembershipTermsClick = () => {
    const event = buildLoyaltyAnalyticsEvent({
      action: AnalyticsAction.Click,
      objectType: AnalyticsObjectType.Cta,
      placementDetail: LandingPagePlacementDetail.MembershipTerms,
      subPage: AnalyticsSubPage.None,
      placementType: AnalyticsPlacementType.Landing,
      parentPage: AnalyticsParentPage.Landing,
    });
    trackSegmentEvent(event.eventName, {
      ...event.data,
      extraData: { experienceVersion: 'v4' },
    });
  };

  return (
    <section
      className={clsx(styles[variant], isNma && styles.nma)}
      data-testid={`${testIdPrefix}:faq`}
      ref={impressionRef}
    >
      {variant === 'signup' && (
        <WaveDivider
          data-testid={`${testIdPrefix}:faq-arch`}
          height={40}
          variant="topArch"
          waveColorClassName={styles.waveFill}
        />
      )}
      <KibContainer data-testid={`${testIdPrefix}:faq-content`}>
        <div className={styles.content}>
          <div className={styles.headingRow}>
            <h2 className={styles.heading}>
              <Trans components={{ strong: <strong /> }} i18nKey={headingI18nKey} />
            </h2>
            <ThemedAsset
              className={styles.faqIllustration}
              darkSrc={LoyaltyAssetMap[LoyaltyAsset.FAQ_QUOTES_DARK]}
              data-testid={`${testIdPrefix}:faq-illustration`}
              height={84}
              lightSrc={LoyaltyAssetMap[LoyaltyAsset.FAQ_QUOTES_LIGHT]}
              width={126}
            />
          </div>
          <CmsContent
            className={styles.cmsContent}
            hideHeader
            selectionKey={cmsSelectionKey}
            zones={[...cmsZones]}
          />
          {variant === 'signup' && (
            <p className={clsx(styles.termsApply, isNma && styles.nma)}>
              <Trans i18nKey="loyalty.sign-up.v4.faq.terms-apply" />{' '}
              <ChewyPlusTerms
                analyticsOverrideCallback={handleMembershipTermsClick}
                showWebExperience={showWebExperience}
                style="inline"
              />
            </p>
          )}
        </div>
      </KibContainer>
    </section>
  );
};
